import { FC, useEffect, useState } from 'react';
import { Alert, Divider, Typography } from '@mui/material';
import { Trans } from 'react-i18next';
import {
  calculateAmountBasedOnBudget,
  calculateFinalPrice,
  calculateNet,
} from '../helpers';
import LoadingButton from '@mui/lab/LoadingButton';
import { UseFormSetValue } from 'react-hook-form';
import { CreateAdFormData } from '@/pages/ad-pages/CreateAd';
import { VAT_FALLBACK } from '../constants';
import {
  SummaryBody,
  SummaryType,
  useGetSummaryPriceMutation,
} from '@/models/summary';
import { ProductType } from '@/models/ad';
import { Button } from '@/components/Button';
import { useGetAreasQuery } from '@/models/location';
import { useGetCountriesQuery } from '@/models/location/query';
import { useProfileQuery } from '@/models/user';
import { FALLBACK_COUNTRY_ID } from '@/features/location/constants';

type Props = {
  type: ProductType;
  zones: number[];
  displays?: number;
  budget?: number | string;
  views?: number;
  countryId?: number;
  areaId?: number;
  onCreate?: () => void;
  onSave?: () => void;
  isUploadingVideo?: boolean;
  isError?: boolean;
  onSetValue: UseFormSetValue<CreateAdFormData>;
  reasonMessage?: string;
};

export const Summary: FC<Props> = ({
  type,
  zones,
  displays,
  views,
  budget,
  areaId,
  onCreate,
  onSave,
  isUploadingVideo,
  onSetValue,
  isError,
  reasonMessage,
}) => {
  const [finalPrice, setFinalPrice] = useState<string>('');
  const [netPrice, setNetPrice] = useState<string>('');
  const [getPrices, { data: prices }] = useGetSummaryPriceMutation();

  const { data: countries } = useGetCountriesQuery();
  const { data: areasData } = useGetAreasQuery({
    countryId: FALLBACK_COUNTRY_ID,
  });
  const { data: user } = useProfileQuery();

  useEffect(() => {
    const payload: SummaryBody = {
      zones,
      type: 'image',
      summaryType: SummaryType.PRICE_SUMMARY,
    };

    if (displays) {
      payload.displays = 1000;
    }

    if (views) {
      payload.views = 1000;
    }

    if (displays || views) {
      getPrices(payload);
    }
  }, [displays, views]);

  useEffect(() => {
    let final = '';

    if (!budget) {
      // TODO: check what properties are needed
      final = calculateFinalPrice({
        prices,
        type,
        zones,
        displays,
        views,
      });
    } else {
      final = budget.toString();
      const result = calculateAmountBasedOnBudget({
        budget,
        prices,
        type,
      });

      if (displays) {
        onSetValue('displays', result);
      }

      if (views) {
        onSetValue('views', result);
      }
    }

    const net = calculateNet({
      prices,
      type,
      zones,
      displays,
      views,
    });

    setFinalPrice(final);
    setNetPrice(net);
  }, [zones, displays, views, type, prices]);

  return (
    <div
      className="p-5 bg-white rounded-md"
      style={{
        boxShadow:
          'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
      }}
    >
      <Typography variant="h6" sx={{ mb: '10px' }}>
        <Trans i18nKey="summary.summary_text" />
      </Typography>
      <Divider />

      <div className="bg-white flex-1 my-5 flex-col rounded-md">
        <Row
          labelKey="label.country"
          value={
            countries?.find((it) => it.id === FALLBACK_COUNTRY_ID)?.name && (
              <Trans
                i18nKey={`country.${countries?.find((it) => it.id === FALLBACK_COUNTRY_ID)?.name}`}
              />
            )
          }
        />

        <Row
          labelKey="label.area"
          value={
            areasData?.find((it) => it.id === areaId)?.name && (
              <Trans
                i18nKey={`area.${areasData?.find((it) => it.id === areaId)?.name}`}
              />
            )
          }
        />

        <Row labelKey="label.zones" value={zones.length && zones.join(', ')} />

        {displays && displays > 0 ? (
          <Row labelKey="summary.product.displays" value={displays} />
        ) : null}

        {views && views > 0 ? (
          <Row labelKey="summary.product.views" value={views} />
        ) : null}

        <Row
          labelKey="label.type"
          value={type && <Trans i18nKey={`summary.media_type.${type}`} />}
        />

        <Row
          labelKey="label.price"
          value={
            netPrice && (
              <>
                {parseFloat(netPrice).toFixed(2)}{' '}
                <Trans i18nKey="common.label.pln" />
              </>
            )
          }
        />

        <Row
          labelKey="label.vat"
          value={
            netPrice && (
              <>
                {(
                  parseFloat(netPrice) *
                  ((prices && prices.vat) || VAT_FALLBACK)
                ).toFixed(2)}{' '}
                <Trans i18nKey="common.label.pln" />
              </>
            )
          }
        />

        <Row
          labelKey="label.total"
          value={
            <div>
              <div className="text-2xl text-primary-500">
                {finalPrice} <Trans i18nKey="common.label.pln" />
              </div>
              <div className="text-sm text-primary-700">
                <Trans i18nKey="summary.vat_info" />
              </div>
            </div>
          }
        />
      </div>
      {reasonMessage && (
        <Alert severity="error" sx={{ mb: '1rem' }}>
          {reasonMessage}
        </Alert>
      )}
      {onCreate && (
        <Button
          variant="contained"
          onClick={() => {
            onCreate && onCreate();
          }}
          disabled={isUploadingVideo || !user?.isProfileComplete}
          loading={isUploadingVideo}
          fullWidth
          color="primary"
          sx={{ mb: '1rem' }}
          type="submit"
        >
          <Trans i18nKey="create_ad.create" />
        </Button>
      )}
      {onSave && (
        <LoadingButton
          onClick={onSave}
          variant="outlined"
          disabled={isUploadingVideo}
          size="large"
          fullWidth
        >
          <Trans i18nKey="create_ad.save_as_draft" />
        </LoadingButton>
      )}
      {isError && (
        <Alert severity="error">
          <Trans i18nKey="summary.footer.errors_detected" />
        </Alert>
      )}
    </div>
  );
};

const Row = ({
  labelKey,
  value,
}: {
  labelKey: string;
  value?: JSX.Element | string | number;
}) => (
  <div className="mb-5 flex flex-row justify-between">
    <div className="w-1/2">
      <Trans i18nKey={labelKey} />
    </div>

    <div className="flex justify-end font-bold w-1/2 text-right">
      {value ? value : <Placeholder />}
    </div>
  </div>
);

const Placeholder = () => <Trans i18nKey="label.not_selected" />;
